import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Mail, Phone, User, Ruler, Edit, Save, X, Search, SlidersHorizontal, ArrowUpDown, ChevronUp, ChevronDown, Trash2 } from "lucide-react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMeasurementsPage = () => {
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMeasurement, setSelectedMeasurement] = useState(null);
  const [detailDialog, setDetailDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedMeasurement, setEditedMeasurement] = useState(null);
  const [sizeCharts, setSizeCharts] = useState({ jacket: [], pant: [], vest: [], shirt: [] });
  const [selectedJacketSize, setSelectedJacketSize] = useState("");
  const [selectedPantSize, setSelectedPantSize] = useState("");
  const [selectedVestSize, setSelectedVestSize] = useState("");
  const [selectedShirtSize, setSelectedShirtSize] = useState("");
  
  // Search, Filter, Sort states
  const [searchQuery, setSearchQuery] = useState("");
  const [sortField, setSortField] = useState("created_at");
  const [sortDirection, setSortDirection] = useState("desc");
  const [filterHasJacket, setFilterHasJacket] = useState("all");
  
  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [measurementToDelete, setMeasurementToDelete] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    // Check auth
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth !== "true") {
      navigate("/admin");
      return;
    }
    fetchMeasurements();
    fetchSizeCharts();
  }, [navigate]);

  const fetchMeasurements = async () => {
    try {
      const response = await axios.get(`${API}/measurements`);
      setMeasurements(response.data);
    } catch (error) {
      console.error("Error fetching measurements:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSizeCharts = async () => {
    try {
      const response = await axios.get(`${API}/size-chart/all`);
      setSizeCharts(response.data);
    } catch (error) {
      console.error("Error fetching size charts:", error);
    }
  };

  // Filter and sort measurements
  const filteredMeasurements = useMemo(() => {
    let filtered = [...measurements];
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(m => 
        m.name?.toLowerCase().includes(query) ||
        m.email?.toLowerCase().includes(query) ||
        m.phone?.includes(query)
      );
    }
    
    // Apply measurement filter
    if (filterHasJacket === "withJacket") {
      filtered = filtered.filter(m => m.chest && parseFloat(m.chest) > 0);
    } else if (filterHasJacket === "withPants") {
      filtered = filtered.filter(m => m.pantsWaist && parseFloat(m.pantsWaist) > 0);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      if (sortField === "created_at") {
        aVal = new Date(a.created_at || 0);
        bVal = new Date(b.created_at || 0);
      } else if (sortField === "name") {
        aVal = (a.name || "").toLowerCase();
        bVal = (b.name || "").toLowerCase();
      } else if (sortField === "email") {
        aVal = (a.email || "").toLowerCase();
        bVal = (b.email || "").toLowerCase();
      }
      
      if (sortDirection === "asc") {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return filtered;
  }, [measurements, searchQuery, sortField, sortDirection, filterHasJacket]);

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const openDetail = (measurement) => {
    setSelectedMeasurement(measurement);
    setEditedMeasurement({ ...measurement });
    setSelectedJacketSize(measurement.jacketSize || "");
    setSelectedPantSize(measurement.pantSize || "");
    setSelectedVestSize(measurement.vestSize || "");
    setSelectedShirtSize(measurement.shirtSize || "");
    setDetailDialog(true);
    setEditMode(false);
  };

  const applyJacketSize = (size) => {
    const sizeData = sizeCharts.jacket.find(s => s.size === size);
    if (sizeData && editedMeasurement) {
      setEditedMeasurement({
        ...editedMeasurement,
        jacketSize: size,
        chest: sizeData.chest,
        stomach: sizeData.stomach,
        finishedShoulder: sizeData.shoulder,
        bicep: sizeData.bicep,
        sleeveLengthLeft: sizeData.sleeve_length,
        sleeveLengthRight: sizeData.sleeve_length,
        backLength: sizeData.back_length,
        frontLength: sizeData.front_length,
        wrist: sizeData.cuff
      });
      setSelectedJacketSize(size);
      toast.success(`Applied jacket size ${size}`);
    }
  };

  const applyPantSize = (size) => {
    const sizeData = sizeCharts.pant.find(s => s.size === size);
    if (sizeData && editedMeasurement) {
      setEditedMeasurement({
        ...editedMeasurement,
        pantSize: size,
        pantsWaist: sizeData.waist,
        pantsHip: sizeData.seat,
        thigh: sizeData.thigh,
        knee: sizeData.knee,
        crotch: sizeData.urise,
        bottomHemWidth: sizeData.bottom
      });
      setSelectedPantSize(size);
      toast.success(`Applied pant size ${size}`);
    }
  };

  const applyVestSize = (size) => {
    const sizeData = sizeCharts.vest.find(s => s.size === size);
    if (sizeData && editedMeasurement) {
      setEditedMeasurement({
        ...editedMeasurement,
        vestSize: size,
        vestChest: sizeData.chest,
        vestStomach: sizeData.stomach,
        vestBackLength: sizeData.back_length,
        vestFrontLength: sizeData.front_length
      });
      setSelectedVestSize(size);
      toast.success(`Applied vest size ${size}`);
    }
  };

  const applyShirtSize = (size) => {
    const sizeData = sizeCharts.shirt.find(s => s.size === size);
    if (sizeData && editedMeasurement) {
      setEditedMeasurement({
        ...editedMeasurement,
        shirtSize: size,
        neck: sizeData.neck,
        shirtChest: sizeData.chest,
        shirtWaist: sizeData.waist,
        shirtSleeve: sizeData.sleeve_length,
        shirtCuff: sizeData.cuff
      });
      setSelectedShirtSize(size);
      toast.success(`Applied shirt size ${size}`);
    }
  };

  const handleSaveMeasurement = async () => {
    try {
      await axios.put(`${API}/measurements/${editedMeasurement.id}`, editedMeasurement);
      setMeasurements(prev => prev.map(m => m.id === editedMeasurement.id ? editedMeasurement : m));
      setSelectedMeasurement(editedMeasurement);
      setEditMode(false);
      toast.success("Measurement updated successfully");
    } catch (error) {
      console.error("Error saving measurement:", error);
      toast.error("Failed to save measurement");
    }
  };

  const handleDeleteClick = (measurement, e) => {
    e.stopPropagation(); // Prevent opening detail dialog
    setMeasurementToDelete(measurement);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!measurementToDelete) return;
    
    try {
      await axios.delete(`${API}/measurements/${measurementToDelete.id}`);
      setMeasurements(prev => prev.filter(m => m.id !== measurementToDelete.id));
      setDeleteDialogOpen(false);
      setMeasurementToDelete(null);
      // Close detail dialog if open
      if (selectedMeasurement?.id === measurementToDelete.id) {
        setDetailDialog(false);
        setSelectedMeasurement(null);
      }
      toast.success("Measurement deleted successfully");
    } catch (error) {
      console.error("Error deleting measurement:", error);
      toast.error("Failed to delete measurement");
    }
  };

  const handleInputChange = (field, value) => {
    setEditedMeasurement(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="py-8 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/admin")}
            className="mb-4"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back to Admin
          </Button>
          <h1 className="font-serif text-3xl tracking-tight">Client Measurements</h1>
          <p className="text-muted-foreground mt-2">
            {filteredMeasurements.length} of {measurements.length} submissions
          </p>
        </div>
      </section>

      {/* Search, Filter, Sort Controls */}
      <section className="py-6 border-b border-white/5 bg-card/30">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-background border-white/10"
              />
            </div>
            
            {/* Filter */}
            <div className="flex items-center gap-2">
              <SlidersHorizontal size={16} className="text-muted-foreground" />
              <Select value={filterHasJacket} onValueChange={setFilterHasJacket}>
                <SelectTrigger className="w-[180px] bg-background border-white/10">
                  <SelectValue placeholder="Filter by..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Measurements</SelectItem>
                  <SelectItem value="withJacket">With Jacket Data</SelectItem>
                  <SelectItem value="withPants">With Pants Data</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Sort */}
            <div className="flex items-center gap-2">
              <ArrowUpDown size={16} className="text-muted-foreground" />
              <div className="flex gap-1">
                <Button
                  variant={sortField === "created_at" ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => toggleSort("created_at")}
                  className="text-xs"
                >
                  Date
                  {sortField === "created_at" && (
                    sortDirection === "desc" ? <ChevronDown size={12} className="ml-1" /> : <ChevronUp size={12} className="ml-1" />
                  )}
                </Button>
                <Button
                  variant={sortField === "name" ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => toggleSort("name")}
                  className="text-xs"
                >
                  Name
                  {sortField === "name" && (
                    sortDirection === "desc" ? <ChevronDown size={12} className="ml-1" /> : <ChevronUp size={12} className="ml-1" />
                  )}
                </Button>
                <Button
                  variant={sortField === "email" ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => toggleSort("email")}
                  className="text-xs"
                >
                  Email
                  {sortField === "email" && (
                    sortDirection === "desc" ? <ChevronDown size={12} className="ml-1" /> : <ChevronUp size={12} className="ml-1" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Measurements List */}
      <section className="py-8">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          {loading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : filteredMeasurements.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {measurements.length === 0 ? "No measurements found" : "No measurements match your search criteria"}
              </p>
              {searchQuery && (
                <Button variant="ghost" onClick={() => setSearchQuery("")} className="mt-2">
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMeasurements.map((m, index) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-card border border-white/10 p-6 hover:border-primary/50 transition-colors cursor-pointer relative group"
                  onClick={() => openDetail(m)}
                >
                  {/* Delete button - appears on hover */}
                  <button
                    onClick={(e) => handleDeleteClick(m, e)}
                    className="absolute top-3 right-3 p-2 rounded-lg bg-destructive/10 text-destructive opacity-0 group-hover:opacity-100 hover:bg-destructive/20 transition-all"
                    title="Delete measurement"
                  >
                    <Trash2 size={16} />
                  </button>
                  
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <User size={20} className="text-primary" />
                    </div>
                    <div>
                      <h3 className="font-medium">{m.name}</h3>
                      <p className="text-xs text-muted-foreground">{m.email}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <p className="flex items-center gap-2">
                      <Phone size={12} />
                      {m.phone}
                    </p>
                    <p className="flex items-center gap-2">
                      <Ruler size={12} />
                      {m.height && m.weight ? `${m.height}" / ${m.weight} lbs` : "No height/weight"}
                    </p>
                    <p className="text-xs">
                      Submitted: {m.created_at && format(parseISO(m.created_at), "MMM d, yyyy")}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Detail Dialog */}
      <Dialog open={detailDialog} onOpenChange={(open) => { setDetailDialog(open); if (!open) setEditMode(false); }}>
        <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="font-serif text-xl">
                {selectedMeasurement?.name}'s Measurements
              </DialogTitle>
              <div className="flex gap-2">
                {editMode ? (
                  <>
                    <Button variant="outline" size="sm" onClick={() => { setEditMode(false); setEditedMeasurement({ ...selectedMeasurement }); }}>
                      <X size={14} className="mr-1" /> Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveMeasurement} className="bg-primary">
                      <Save size={14} className="mr-1" /> Save
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setEditMode(true)}>
                    <Edit size={14} className="mr-1" /> Edit
                  </Button>
                )}
              </div>
            </div>
          </DialogHeader>
          
          {selectedMeasurement && (
            <div className="space-y-6 py-4">
              {/* Size Selection Section */}
              {editMode && (
                <div className="bg-primary/5 border border-primary/20 p-4 rounded-lg">
                  <h4 className="mono-text text-primary text-xs mb-4">APPLY SIZE FROM CHART</h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Jacket Size</label>
                      <Select value={selectedJacketSize} onValueChange={applyJacketSize}>
                        <SelectTrigger className="bg-background border-white/10">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          {sizeCharts.jacket.map(s => (
                            <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Pant Size</label>
                      <Select value={selectedPantSize} onValueChange={applyPantSize}>
                        <SelectTrigger className="bg-background border-white/10">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          {sizeCharts.pant.map(s => (
                            <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Vest Size</label>
                      <Select value={selectedVestSize} onValueChange={applyVestSize}>
                        <SelectTrigger className="bg-background border-white/10">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          {sizeCharts.vest.map(s => (
                            <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Shirt Size</label>
                      <Select value={selectedShirtSize} onValueChange={applyShirtSize}>
                        <SelectTrigger className="bg-background border-white/10">
                          <SelectValue placeholder="Select size" />
                        </SelectTrigger>
                        <SelectContent>
                          {sizeCharts.shirt.map(s => (
                            <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Selecting a size will auto-fill the corresponding measurements below
                  </p>
                </div>
              )}

              {/* Applied Sizes Display */}
              {!editMode && (selectedMeasurement.jacketSize || selectedMeasurement.pantSize || selectedMeasurement.vestSize || selectedMeasurement.shirtSize) && (
                <div className="bg-indigo-500/10 border border-indigo-500/20 p-3 rounded-lg">
                  <h4 className="mono-text text-indigo-400 text-xs mb-2">APPLIED SIZES</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedMeasurement.jacketSize && (
                      <span className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded">
                        Jacket: {selectedMeasurement.jacketSize}
                      </span>
                    )}
                    {selectedMeasurement.pantSize && (
                      <span className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded">
                        Pant: {selectedMeasurement.pantSize}
                      </span>
                    )}
                    {selectedMeasurement.vestSize && (
                      <span className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded">
                        Vest: {selectedMeasurement.vestSize}
                      </span>
                    )}
                    {selectedMeasurement.shirtSize && (
                      <span className="text-xs bg-indigo-500/20 text-indigo-300 px-2 py-1 rounded">
                        Shirt: {selectedMeasurement.shirtSize}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Info */}
              <div>
                <h4 className="mono-text text-primary text-xs mb-3">Contact Info</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <p><span className="text-muted-foreground">Email:</span> {selectedMeasurement.email}</p>
                  <p><span className="text-muted-foreground">Phone:</span> {selectedMeasurement.phone}</p>
                  <p><span className="text-muted-foreground">Height:</span> {selectedMeasurement.height}"</p>
                  <p><span className="text-muted-foreground">Weight:</span> {selectedMeasurement.weight} lbs</p>
                </div>
              </div>

              {/* Fit Preferences */}
              <div>
                <h4 className="mono-text text-primary text-xs mb-3">Fit Preferences</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <p><span className="text-muted-foreground">Fit:</span> {selectedMeasurement.fit}</p>
                  <p><span className="text-muted-foreground">Stomach:</span> {selectedMeasurement.stomachType}</p>
                  <p><span className="text-muted-foreground">Shoulder:</span> {selectedMeasurement.shoulderType}</p>
                  <p><span className="text-muted-foreground">Seat:</span> {selectedMeasurement.seatType}</p>
                </div>
              </div>

              {/* Jacket Measurements */}
              <div>
                <h4 className="mono-text text-primary text-xs mb-3">Jacket Measurements</h4>
                {editMode ? (
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { field: "neck", label: "Neck" },
                      { field: "chest", label: "Chest" },
                      { field: "stomach", label: "Stomach" },
                      { field: "hip", label: "Hip" },
                      { field: "finishedShoulder", label: "Shoulder" },
                      { field: "frontLength", label: "Front Length" },
                      { field: "backLength", label: "Back Length" },
                      { field: "bicep", label: "Bicep" },
                      { field: "wrist", label: "Wrist/Cuff" },
                      { field: "sleeveLengthLeft", label: "Sleeve L" },
                      { field: "sleeveLengthRight", label: "Sleeve R" },
                    ].map(({ field, label }) => (
                      <div key={field}>
                        <label className="text-xs text-muted-foreground">{label}</label>
                        <Input
                          type="number"
                          step="0.25"
                          value={editedMeasurement[field] || ""}
                          onChange={(e) => handleInputChange(field, parseFloat(e.target.value) || "")}
                          className="bg-background border-white/10 h-8 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <p><span className="text-muted-foreground">Neck:</span> {selectedMeasurement.neck}"</p>
                    <p><span className="text-muted-foreground">Chest:</span> {selectedMeasurement.chest}"</p>
                    <p><span className="text-muted-foreground">Stomach:</span> {selectedMeasurement.stomach}"</p>
                    <p><span className="text-muted-foreground">Hip:</span> {selectedMeasurement.hip}"</p>
                    <p><span className="text-muted-foreground">Shoulder:</span> {selectedMeasurement.finishedShoulder}"</p>
                    <p><span className="text-muted-foreground">Front Length:</span> {selectedMeasurement.frontLength}"</p>
                    <p><span className="text-muted-foreground">Back Length:</span> {selectedMeasurement.backLength}"</p>
                    <p><span className="text-muted-foreground">Bicep:</span> {selectedMeasurement.bicep}"</p>
                    <p><span className="text-muted-foreground">Wrist:</span> {selectedMeasurement.wrist}"</p>
                    <p><span className="text-muted-foreground">Sleeve L:</span> {selectedMeasurement.sleeveLengthLeft}"</p>
                    <p><span className="text-muted-foreground">Sleeve R:</span> {selectedMeasurement.sleeveLengthRight}"</p>
                  </div>
                )}
              </div>

              {/* Pants Measurements */}
              <div>
                <h4 className="mono-text text-primary text-xs mb-3">Pants Measurements</h4>
                {editMode ? (
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { field: "pantsWaist", label: "Waist" },
                      { field: "pantsHip", label: "Hip/Seat" },
                      { field: "thigh", label: "Thigh" },
                      { field: "knee", label: "Knee" },
                      { field: "crotch", label: "Crotch/Rise" },
                      { field: "leftOuterSeam", label: "Outseam L" },
                      { field: "rightOuterSeam", label: "Outseam R" },
                      { field: "bottomHemWidth", label: "Hem/Bottom" },
                    ].map(({ field, label }) => (
                      <div key={field}>
                        <label className="text-xs text-muted-foreground">{label}</label>
                        <Input
                          type="number"
                          step="0.25"
                          value={editedMeasurement[field] || ""}
                          onChange={(e) => handleInputChange(field, parseFloat(e.target.value) || "")}
                          className="bg-background border-white/10 h-8 text-sm"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <p><span className="text-muted-foreground">Waist:</span> {selectedMeasurement.pantsWaist}"</p>
                    <p><span className="text-muted-foreground">Hip:</span> {selectedMeasurement.pantsHip}"</p>
                    <p><span className="text-muted-foreground">Thigh:</span> {selectedMeasurement.thigh}"</p>
                    <p><span className="text-muted-foreground">Knee:</span> {selectedMeasurement.knee}"</p>
                    <p><span className="text-muted-foreground">Crotch:</span> {selectedMeasurement.crotch}"</p>
                    <p><span className="text-muted-foreground">Outseam L:</span> {selectedMeasurement.leftOuterSeam}"</p>
                    <p><span className="text-muted-foreground">Outseam R:</span> {selectedMeasurement.rightOuterSeam}"</p>
                    <p><span className="text-muted-foreground">Hem:</span> {selectedMeasurement.bottomHemWidth}"</p>
                  </div>
                )}
              </div>

              {/* Photos */}
              {selectedMeasurement.photos && selectedMeasurement.photos.length > 0 && (
                <div>
                  <h4 className="mono-text text-primary text-xs mb-3">Photos ({selectedMeasurement.photos.length})</h4>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedMeasurement.photos.map((photo, i) => (
                      <img 
                        key={i} 
                        src={photo} 
                        alt={`Client photo ${i + 1}`}
                        className="w-full h-32 object-cover border border-white/10"
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              {selectedMeasurement.notes && (
                <div>
                  <h4 className="mono-text text-primary text-xs mb-3">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedMeasurement.notes}</p>
                </div>
              )}
              
              {/* Delete Button in Dialog */}
              <div className="pt-4 border-t border-white/10">
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setMeasurementToDelete(selectedMeasurement);
                    setDeleteDialogOpen(true);
                  }}
                  className="w-full"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete This Measurement
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Measurement</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the measurement for{" "}
              <span className="font-semibold text-foreground">{measurementToDelete?.name}</span>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminMeasurementsPage;
